import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, LogOut, Plus, FileText, ChevronRight } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function HomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedShift, setSelectedShift] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentReports, setRecentReports] = useState([]);

  const todayStr = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    const load = async () => {
      try {
        const [lookups, reports] = await Promise.all([
          api.get("/lookups"),
          api.get("/reports"),
        ]);
        setSites(lookups.sites || []);
        setRecentReports((reports || []).slice(0, 5));
      } catch (err) {
        toast.error("Failed to load data");
      }
    };
    load();
  }, []);

  const handleCreateResume = async () => {
    if (!selectedSite || !selectedShift) {
      toast.error("Select site and shift");
      return;
    }
    setLoading(true);
    try {
      // Get GPS coordinates for geofence check
      let latitude = null, longitude = null;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
        );
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } catch (geoErr) {
        // GPS unavailable — proceed without coordinates (geofence won't block if no coords)
      }
      const res = await api.post("/reports", {
        report_date: todayStr,
        site_id: selectedSite,
        shift: selectedShift,
        latitude,
        longitude,
      });
      const report = res.report;
      toast.success("New report created");
      navigate(`/report/${report.id}/header`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to create report";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    if (status === "Submitted") return "bg-primary/20 text-primary border-primary/30";
    if (status === "Saved") return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Top Bar */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="font-headline text-3xl font-bold tracking-tight mb-2">Daily Report</h1>
        <p className="text-muted-foreground text-sm mb-10">Create or resume a daily contractor report</p>

        <Card className="border-border bg-card mb-8">
          <CardContent className="pt-6 space-y-6">
            {/* Locked Date */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Report Date</Label>
              <div className="flex items-center h-10 w-full rounded-md border border-border bg-background px-3" data-testid="locked-date-display">
                <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="font-mono text-sm">{todayStr}</span>
                <span className="ml-auto text-xs text-muted-foreground">Locked to today</span>
              </div>
            </div>

            {/* Site Select */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger data-testid="site-select-trigger" className="bg-background border-border">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shift Select */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Shift</Label>
              <Select value={selectedShift} onValueChange={setSelectedShift}>
                <SelectTrigger data-testid="shift-select-trigger" className="bg-background border-border">
                  <SelectValue placeholder="Select shift" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Day">Day</SelectItem>
                  <SelectItem value="Night">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleCreateResume}
              data-testid="create-resume-report-button"
              className="w-full font-headline font-semibold h-11"
              disabled={loading || !selectedSite || !selectedShift}
            >
              <Plus className="h-4 w-4 mr-2" strokeWidth={2} />
              {loading ? "Loading..." : "Create New Daily Report"}
            </Button>
          </CardContent>
        </Card>

        {/* Recent Reports */}
        {recentReports.length > 0 && (
          <div>
            <h2 className="font-headline text-lg font-semibold mb-4">Recent Reports</h2>
            <div className="space-y-2">
              {recentReports.map((r) => (
                <Card
                  key={r.id}
                  className="border-border bg-card hover:bg-accent/30 transition-colors cursor-pointer"
                  onClick={() => navigate(r.status === "Submitted" ? `/report/${r.id}/review` : `/report/${r.id}/dashboard`)}
                  data-testid={`recent-report-${r.id}`}
                >
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <div>
                        <span className="font-mono text-sm">{r.report_date}</span>
                        <span className="text-muted-foreground text-xs ml-3">{r.shift} Shift</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={statusColor(r.status)}>{r.status}</Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
